/**
 * Pure utility functions for chart rendering and data formatting.
 * No React or DOM dependencies — fully testable.
 */

// ── LTTB Downsampling ───────────────────────────────────────────

type TimePoint = { ts: number; value: number };

/**
 * Largest-Triangle-Three-Buckets (LTTB) downsampling algorithm.
 * Reduces a time series to `maxPoints` while preserving visual shape.
 */
export function downsampleTimeSeries(data: TimePoint[], maxPoints: number): TimePoint[] {
  if (data.length <= maxPoints || maxPoints < 3) return data;

  const bucketSize = (data.length - 2) / (maxPoints - 2);
  const result: TimePoint[] = [data[0]!]; // always keep first

  for (let i = 0; i < maxPoints - 2; i++) {
    const bucketStart = Math.floor((i + 0) * bucketSize) + 1;
    const bucketEnd = Math.floor((i + 1) * bucketSize) + 1;
    const nextBucketStart = Math.floor((i + 1) * bucketSize) + 1;
    const nextBucketEnd = Math.min(Math.floor((i + 2) * bucketSize) + 1, data.length - 1);

    // Average of next bucket (the "C" point in LTTB)
    let avgTs = 0;
    let avgVal = 0;
    let nextCount = 0;
    for (let j = nextBucketStart; j < nextBucketEnd; j++) {
      avgTs += data[j]!.ts;
      avgVal += data[j]!.value;
      nextCount++;
    }
    if (nextCount > 0) {
      avgTs /= nextCount;
      avgVal /= nextCount;
    }

    // Find the point in current bucket with the largest triangle area
    const prev = result[result.length - 1]!;
    let maxArea = -1;
    let bestIdx = bucketStart;

    for (let j = bucketStart; j < bucketEnd; j++) {
      const d = data[j]!;
      const area = Math.abs(
        (prev.ts - avgTs) * (d.value - prev.value) - (prev.ts - d.ts) * (avgVal - prev.value),
      );
      if (area > maxArea) {
        maxArea = area;
        bestIdx = j;
      }
    }

    result.push(data[bestIdx]!);
  }

  result.push(data[data.length - 1]!); // always keep last
  return result;
}

// ── Percentiles ─────────────────────────────────────────────────

/**
 * Compute percentiles from a numeric array.
 * @param values - unsorted input values
 * @param percentiles - array of percentiles to compute (0–100)
 * @returns object mapping each percentile to its computed value
 */
export function computePercentiles(
  values: number[],
  percentiles: number[],
): Record<number, number> {
  if (values.length === 0) {
    return Object.fromEntries(percentiles.map((p) => [p, 0]));
  }
  const sorted = [...values].sort((a, b) => a - b);
  const result: Record<number, number> = {};
  for (const p of percentiles) {
    const idx = (p / 100) * (sorted.length - 1);
    const lo = Math.floor(idx);
    const hi = Math.ceil(idx);
    const frac = idx - lo;
    result[p] = sorted[lo]! + frac * (sorted[hi]! - sorted[lo]!);
  }
  return result;
}

// ── Formatters ──────────────────────────────────────────────────

export function formatBytes(n: number): string {
  if (n < 0) return `-${formatBytes(-n)}`;
  if (n === 0) return "0 B";
  const units = ["B", "KB", "MB", "GB", "TB"];
  const i = Math.min(Math.floor(Math.log(n) / Math.log(1024)), units.length - 1);
  const val = n / 1024 ** i;
  return `${val < 10 ? val.toFixed(1) : Math.round(val)} ${units[i]}`;
}

export function formatCurrency(cents: number): string {
  const abs = Math.abs(cents);
  const sign = cents < 0 ? "-" : "";
  if (abs < 100) return `${sign}$0.${String(abs).padStart(2, "0")}`;
  const dollars = Math.floor(abs / 100);
  const remainder = abs % 100;
  return `${sign}$${dollars.toLocaleString()}.${String(remainder).padStart(2, "0")}`;
}

export function formatDuration(ms: number): string {
  if (ms < 1000) return `${Math.round(ms)}ms`;
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)}s`;
  const mins = Math.floor(ms / 60_000);
  const secs = Math.round((ms % 60_000) / 1000);
  return `${mins}m ${secs}s`;
}

export function formatPercent(ratio: number): string {
  const pct = ratio * 100;
  if (pct >= 99.95) return "100%";
  if (pct < 0.01 && pct > 0) return "<0.01%";
  return `${pct < 10 ? pct.toFixed(1) : Math.round(pct)}%`;
}

// ── Color Scale ─────────────────────────────────────────────────

/**
 * Command Deck multi-series palette — replaces the old rainbow
 * `hsl(h,70%,50%)` scale (which clashed with the Command Deck theme).
 * Anchored on the cyan signal, then analogous cyans/teals/sky plus the
 * status hues, so series read as console telemetry rather than a
 * rainbow. Deterministic + pure: index `i` always maps to
 * `CHART_SERIES_PALETTE[i % len]`.
 */
export const CHART_SERIES_PALETTE = [
  "#22d3ee", // signal cyan
  "#2dd4bf", // teal
  "#38bdf8", // sky
  "#34d399", // emerald (healthy-adjacent)
  "#fbbf24", // amber (warning-adjacent)
  "#fb7185", // rose (critical-adjacent)
  "#a78bfa", // violet
  "#94a3b8", // slate (muted series)
] as const;

export function generateColorScale(count: number): string[] {
  if (count <= 0) return [];
  return Array.from(
    { length: count },
    (_, i) => CHART_SERIES_PALETTE[i % CHART_SERIES_PALETTE.length],
  );
}

// ── Trend Detection ─────────────────────────────────────────────

export type Trend = "up" | "down" | "flat";

/**
 * Determine the trend direction from a sequence of values.
 * Compares the average of the latter half to the first half.
 * @param values - ordered data points
 * @param threshold - minimum relative change to not be "flat" (default 0.05 = 5%)
 */
export function calculateTrend(values: number[], threshold = 0.05): Trend {
  if (values.length < 2) return "flat";
  const mid = Math.floor(values.length / 2);
  const firstHalf = values.slice(0, mid);
  const secondHalf = values.slice(mid);
  const avg = (arr: number[]) => arr.reduce((s, v) => s + v, 0) / arr.length;
  const avgFirst = avg(firstHalf);
  const avgSecond = avg(secondHalf);
  if (avgFirst === 0) return avgSecond > 0 ? "up" : "flat";
  const change = (avgSecond - avgFirst) / Math.abs(avgFirst);
  if (change > threshold) return "up";
  if (change < -threshold) return "down";
  return "flat";
}
